import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { api, ApiError, type Reminder } from '../lib/api';
import { Composer, type MicSignal } from '../components/Composer';
import { Icon, type IconName } from '../components/Icon';
import { Waveform, type WaveMode } from '../components/Waveform';
import {
  greeting,
  statusLabel,
  time,
  usd,
  weatherGlyph,
  type CalendarEvent,
  type WeatherReport,
} from '@elena/shared';
import { eventTime } from './Calendar';
import { session } from '../lib/session';
import { useElena } from '../state';
import { ElenaAvatar } from '../components/ElenaAvatar';
import { RevealText } from '../components/RevealText';
import { useLetterIntro } from '../lib/motion';

const suggestions = [
  'Upali FiveM i uđi na server',
  'Pusti Cecu na YouTube-u',
  'Ko je sad na serveru?',
  'Otvori Discord',
];

export function Home({ micSignal }: { micSignal: MicSignal }) {
  const { messages, thinking, spokenTurn, send, connection, error, setTalk } = useElena();
  const [interim, setInterim] = useState<string | null>(null);
  const title = useRef<HTMLHeadingElement>(null);
  useLetterIntro(title);

  const lastElena = [...messages].reverse().find((m) => m.role === 'elena');
  const lastUser = [...messages].reverse().find((m) => m.role === 'user');
  const mode: WaveMode =
    interim !== null
      ? 'listening'
      : thinking
        ? 'thinking'
        : lastElena && lastElena.id !== spokenTurn
          ? 'speaking'
          : 'idle';

  return (
    <div className="page home">
      <header className="home-intro">
        <div>
          <span className="home-eyebrow">MILPET / PERSONAL AI</span>
          <h1 ref={title}>ELENA</h1>
          <p>Tvoj glas. Tvoj svet. Sve na jednom mestu.</p>
        </div>
        <span className="home-connection">
          <span className={`dot ${connection === 'online' ? 'on' : 'warn'}`} />
          {connection === 'online' ? 'Povezana' : 'Povezujem se…'}
        </span>
      </header>
      <section className="console card" aria-live="polite">
        <div className="console-meta">
          <span className={`dot ${mode === 'idle' ? 'warn' : 'brass'}`} />
          <span>
            {interim !== null
              ? 'Slušam'
              : thinking
                ? 'Elena razmišlja'
                : lastElena
                  ? 'Elena'
                  : 'Spremna'}
          </span>
          <span className="push row gap-8">
            {lastElena && <span className="muted">{time(lastElena.at)}</span>}
            <a href="#/chat" className="small">
              Ceo razgovor
            </a>
          </span>
        </div>

        <div className="console-presence">
          <ElenaAvatar mode={mode} />
          <div className="console-conversation">
            {interim !== null ? (
              <p className="console-live">{interim || 'Pričaj…'}</p>
            ) : (
              <>
                {lastUser && <p className="console-user">{lastUser.text}</p>}
                <RevealText
                  className={`console-reply${(lastElena?.text.length ?? 0) > 110 ? ' is-long' : ''}`}
                  text={lastElena?.text ?? greeting()}
                />
              </>
            )}
          </div>
        </div>

        <Waveform mode={mode} />

        {interim === null && lastElena?.steps && lastElena.steps.length > 0 && (
          <ul className="steps">
            {lastElena.steps.map((s, i) => (
              <li key={i} className={`step step-${s.status}`}>
                <Icon
                  name={s.status === 'done' ? 'check' : s.status === 'denied' ? 'shield' : 'alert'}
                  size={16}
                  stroke={2.2}
                />
                <span className="grow">{s.summary}</span>
                <span className="step-status">{statusLabel[s.status]}</span>
              </li>
            ))}
          </ul>
        )}

        {error && <p className="notice">{error}</p>}

        <div className="chips">
          <button
            type="button"
            className="chip chip-talk"
            onClick={() => setTalk(true)}
            disabled={connection !== 'online'}
          >
            <Icon name="talk" size={16} /> Hajde da pričamo
          </button>
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              className="chip"
              onClick={() => send(s, false)}
              disabled={connection !== 'online'}
            >
              {s}
            </button>
          ))}
        </div>

        <Composer onListening={setInterim} listenSignal={micSignal} />
      </section>

      <div className="home-side">
        <RemoteCard />
        <WeatherCard />
        <TodayCard />
        <ServerCard />
        <RemindersCard />
        <DevicesCard />
        <ActivityCard />
        <BudgetCard />
      </div>
    </div>
  );
}

function ServerCard() {
  const { status } = useElena();
  const s = status?.server;
  return (
    <section className="card server-card" aria-labelledby="srv-title">
      <header className="card-head">
        <h2 id="srv-title">{s?.name ?? 'Server'}</h2>
        {s && (
          <span className={`pill ${s.online ? 'ok' : 'bad'}`}>
            <span className={`dot ${s.online ? 'on' : 'off'}`} />
            {s.online ? 'Online' : 'Ne odgovara'}
          </span>
        )}
      </header>
      {s?.online ? (
        <>
          <div className="big-number">
            <span className="num">{s.players.length}</span>
            <span className="muted">{s.maxPlayers ? `od ${s.maxPlayers} igrača` : 'igrača'}</span>
          </div>
          <div
            className="meter"
            role="meter"
            aria-valuemin={0}
            aria-valuemax={s.maxPlayers ?? 64}
            aria-valuenow={s.players.length}
            aria-label="Popunjenost servera"
          >
            <span
              style={{
                width: `${Math.min(100, (s.players.length / (s.maxPlayers ?? 64)) * 100)}%`,
              }}
            />
          </div>
          <p className="muted small">
            {s.resources ? `${s.resources} resursa · ` : ''}provereno u {time(s.checkedAt)}
          </p>
        </>
      ) : (
        <p className="muted">{status ? 'FiveM server trenutno ne odgovara.' : 'Učitavam…'}</p>
      )}
      <a className="btn primary" href="#/server">
        Igrači i detalji
      </a>
    </section>
  );
}

interface RemoteButton {
  label: string;
  action: string;
  params?: Record<string, unknown>;
  icon?: IconName;
}

const tvButtons: RemoteButton[] = [
  { label: 'Upali', action: 'tv.power', params: { state: 'on' }, icon: 'power' },
  { label: 'Ugasi', action: 'tv.power', params: { state: 'off' }, icon: 'stop' },
  { label: 'Netflix', action: 'tv.app', params: { app: 'Netflix' } },
  { label: 'YouTube', action: 'tv.app', params: { app: 'YouTube' } },
  { label: 'Tiše', action: 'tv.control', params: { command: 'volume_down' }, icon: 'minus' },
  { label: 'Glasnije', action: 'tv.control', params: { command: 'volume_up' }, icon: 'plus' },
  { label: 'Bez zvuka', action: 'tv.control', params: { command: 'mute' }, icon: 'mute' },
  { label: 'Pauza', action: 'tv.control', params: { command: 'pause' }, icon: 'pause' },
];
const ps5Buttons: RemoteButton[] = [
  { label: 'Upali PS5', action: 'ps5.power', params: { state: 'on' }, icon: 'game' },
  { label: 'Mirovanje', action: 'ps5.power', params: { state: 'off' }, icon: 'stop' },
  { label: 'Stanje', action: 'ps5.status' },
];

/** TV and PS5 remote, shown only when the PC agent has them configured. */
function RemoteCard() {
  const { devices } = useElena();
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const caps = new Set(devices.filter((d) => d.kind === 'agent').flatMap((d) => d.capabilities));
  const hasTv = caps.has('tv.power');
  const hasPs5 = caps.has('ps5.power');
  if (!hasTv && !hasPs5) return null;

  const press = async (b: RemoteButton) => {
    setBusy(b.label);
    setNote(null);
    try {
      const r = await api.runAction(b.action, b.params ?? {}, session.origin());
      setNote({ ok: r.ok, text: r.message });
    } catch (err) {
      setNote({ ok: false, text: err instanceof ApiError ? err.message : 'Nije uspelo.' });
    } finally {
      setBusy(null);
    }
  };

  const group = (title: string, buttons: RemoteButton[]) => (
    <div className="remote-group">
      <span className="remote-title">{title}</span>
      <div className="remote-grid">
        {buttons.map((b) => (
          <button
            key={b.label}
            type="button"
            className="remote-btn"
            onClick={() => void press(b)}
            disabled={busy !== null}
            aria-busy={busy === b.label}
          >
            {b.icon && <Icon name={b.icon} size={16} />}
            {busy === b.label ? '…' : b.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <section className="card" aria-labelledby="remote-title">
      <header className="card-head">
        <h2 id="remote-title">Daljinski</h2>
        <span className="muted small">„pusti Netflix na TV-u“</span>
      </header>
      {hasTv && group('LG TV', tvButtons)}
      {hasPs5 && group('PlayStation 5', ps5Buttons)}
      {note && (
        <p className={note.ok ? 'notice ok' : 'notice'} role="status">
          {note.text}
        </p>
      )}
    </section>
  );
}

function WeatherCard() {
  const [report, setReport] = useState<WeatherReport | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api
        .weather()
        .then((r) => alive && (setReport(r.report), setFailed(false)))
        .catch(() => alive && setFailed(true));
    void load();
    const timer = window.setInterval(load, 15 * 60_000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, []);

  if (failed && !report) return null;
  const f = report?.forecast;
  return (
    <section className="card weather-card" aria-labelledby="wx-title">
      <header className="card-head">
        <h2 id="wx-title">{report ? report.place.name : 'Vreme'}</h2>
        {f && (
          <span className="muted small">
            {Math.round(f.min)}° / {Math.round(f.max)}°
          </span>
        )}
      </header>
      {report ? (
        <>
          <div className="weather-now">
            <span className="weather-glyph" aria-hidden="true">
              {weatherGlyph(report.now?.code ?? f!.code)}
            </span>
            <span className="num">{Math.round(report.now?.temp ?? f!.max)}°</span>
            <span className="muted">
              {report.now
                ? `${report.now.description}, oseća se ${Math.round(report.now.feels)}°`
                : f!.description}
            </span>
          </div>
          <p className="weather-advice">{report.advice.join(' ')}</p>
          {f && f.rainChance >= 20 && (
            <p className="muted small">
              Padavine {f.rainChance}%{f.rainAround ? `, najviše oko ${f.rainAround}h` : ''}
            </p>
          )}
        </>
      ) : (
        <p className="muted">Učitavam prognozu…</p>
      )}
    </section>
  );
}

function TodayCard() {
  const { activity } = useElena();
  const [events, setEvents] = useState<CalendarEvent[] | null>(null);
  const [configured, setConfigured] = useState(true);

  useEffect(() => {
    api
      .calendarEvents(2)
      .then((r) => {
        setConfigured(r.configured);
        setEvents(r.events);
      })
      .catch(() => setEvents([]));
  }, [activity.length]);

  if (!configured) return null;
  const now = Date.now();
  const upcoming = (events ?? []).filter((e) => new Date(e.end).getTime() > now).slice(0, 5);
  return (
    <section className="card" aria-labelledby="today-title">
      <header className="card-head">
        <h2 id="today-title">Kalendar</h2>
        <a href="#/calendar" className="small">
          Sve
        </a>
      </header>
      {events && upcoming.length === 0 && (
        <p className="muted">Danas i sutra nemaš ništa. Reci „ubaci trening sutra u 18h“.</p>
      )}
      <ul className="list">
        {upcoming.map((e) => (
          <li key={e.id} className="list-row">
            <Icon name="calendar" />
            <span className="grow">
              {e.title}
              <span className="muted small block">
                {e.day === new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Belgrade' })
                  ? 'danas'
                  : 'sutra'}{' '}
                · {eventTime(e)}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function DevicesCard() {
  const { devices } = useElena();
  const pc = devices.find((d) => d.kind === 'agent');
  return (
    <section className="card" aria-labelledby="dev-title">
      <header className="card-head">
        <h2 id="dev-title">Uređaji</h2>
        <span className="muted small">{devices.length} povezano</span>
      </header>
      {!pc && (
        <p className="notice subtle">
          Računar nije povezan. Pokreni Elena agenta na PC-u da bi radile muzika i FiveM.
        </p>
      )}
      <ul className="list">
        {devices.map((d) => (
          <li key={d.deviceId} className="list-row">
            <Icon name={d.kind === 'agent' ? 'monitor' : d.kind === 'mobile' ? 'phone' : 'chat'} />
            <span className="grow">{d.name}</span>
            <span className="muted small">
              {d.kind === 'agent' ? `${d.capabilities.length} akcija` : 'aplikacija'}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ActivityCard() {
  const { activity } = useElena();
  return (
    <section className="card" aria-labelledby="act-title">
      <header className="card-head">
        <h2 id="act-title">Aktivnost</h2>
      </header>
      {activity.length === 0 ? (
        <p className="muted">Kad Elena nešto uradi, videćeš to ovde.</p>
      ) : (
        <ul className="list">
          {activity.slice(0, 6).map((a) => (
            <li key={a.id} className="list-row">
              <span className="muted small mono">{time(a.at)}</span>
              <span className="grow">{a.summary}</span>
              <span className={`tag tag-${a.status}`}>{statusLabel[a.status]}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function BudgetCard() {
  const { status } = useElena();
  const b = status?.budget;
  if (!b) return null;
  const pct = b.limitUsd ? Math.min(100, (b.usd / b.limitUsd) * 100) : 0;
  return (
    <section className="card" aria-labelledby="bud-title">
      <header className="card-head">
        <h2 id="bud-title">Claude potrošnja</h2>
        <span className="muted small">{b.month}</span>
      </header>
      <div className="big-number">
        <span className="num">{usd(b.usd)}</span>
        <span className="muted">{b.limitUsd ? `od ${usd(b.limitUsd)}` : 'bez limita'}</span>
      </div>
      <div
        className="meter"
        role="meter"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
        aria-label="Iskorišćen budžet"
      >
        <span style={{ width: `${pct}%` }} />
      </div>
      <p className="muted small">{b.requests} zahteva ovog meseca</p>
      {status.speech && (
        <p className="muted small">
          Azure besplatno: prepoznavanje {Math.round(status.speech.stt.usedSeconds / 60)} od{' '}
          {Math.round(status.speech.stt.limitSeconds / 60)} min, glas{' '}
          {Math.round(status.speech.tts.usedChars / 1000)}k od{' '}
          {Math.round(status.speech.tts.limitChars / 1000)}k znakova
        </p>
      )}
    </section>
  );
}

const reminderWhen = (iso: string) => {
  const at = new Date(iso);
  const opts = { timeZone: 'Europe/Belgrade' } as const;
  const day = (d: Date) => d.toLocaleDateString('sr-Latn-RS', opts);
  const clock = at.toLocaleTimeString('sr-Latn-RS', {
    ...opts,
    hour: '2-digit',
    minute: '2-digit',
  });
  const now = new Date();
  if (day(at) === day(now)) return `danas ${clock}`;
  if (day(at) === day(new Date(now.getTime() + 86_400_000))) return `sutra ${clock}`;
  return `${at.toLocaleDateString('sr-Latn-RS', { ...opts, weekday: 'short', day: 'numeric', month: 'numeric' })} ${clock}`;
};

function RemindersCard() {
  const { activity } = useElena();
  const [list, setList] = useState<Reminder[] | null>(null);
  const [text, setText] = useState('');
  const [at, setAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setList((await api.reminders()).pending);
    } catch {
      setList([]);
    }
  }, []);

  // Reload when Elena does something (a reminder set by voice shows up right away).
  useEffect(() => {
    void load();
  }, [load, activity.length]);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      // datetime-local is the browser's local time; send it as an exact instant.
      await api.addReminder(text.trim(), new Date(at).toISOString());
      setText('');
      setAt('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Podsetnik nije sačuvan.');
    }
  };

  return (
    <section className="card" aria-labelledby="rem-title">
      <header className="card-head">
        <h2 id="rem-title">Podsetnici</h2>
        <span className="muted small">{list ? `${list.length} zakazano` : ' '}</span>
      </header>
      {list && list.length === 0 && (
        <p className="muted">Reci „podseti me u 20h da restartujem server“ ili dodaj ispod.</p>
      )}
      {list && list.length > 0 && (
        <ul className="list">
          {list.slice(0, 6).map((r) => (
            <li key={r.id} className="list-row">
              <Icon name="clock" />
              <span className="grow">
                {r.text}
                <span className="muted small block">{reminderWhen(r.at)}</span>
              </span>
              <button
                type="button"
                className="icon-btn ghost"
                aria-label={`Otkaži: ${r.text}`}
                onClick={async () => {
                  await api.removeReminder(r.id).catch(() => undefined);
                  await load();
                }}
              >
                <Icon name="x" size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <form className="stack-12" onSubmit={add}>
        <label className="sr-only" htmlFor="rem-text">
          Na šta da te podsetim
        </label>
        <input
          id="rem-text"
          className="field"
          placeholder="Na šta da te podsetim"
          value={text}
          maxLength={200}
          onChange={(e) => setText(e.target.value)}
          required
        />
        <div className="row gap-8">
          <label className="sr-only" htmlFor="rem-at">
            Kada
          </label>
          <input
            id="rem-at"
            type="datetime-local"
            className="field time-field grow-field"
            value={at}
            onChange={(e) => setAt(e.target.value)}
            required
          />
          <button type="submit" className="btn primary">
            Dodaj
          </button>
        </div>
        {error && <p className="notice">{error}</p>}
      </form>
    </section>
  );
}
